/** Pieces shared by the public booking page, the portal and staff: times in the firm's zone and the slot picker. */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Clock, Phone, Video } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SMS_DISCLOSURE, smsConsentText } from "@clientflow/shared";

export const money = (cents: number) => (cents % 100 === 0 ? `$${(cents / 100).toLocaleString("en-US")}` : `$${(cents / 100).toFixed(2)}`);

/** "Mon, Jan 11" for a day in the firm's zone. */
export function dayLabel(iso: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }).format(new Date(iso));
}

/** "10:30 AM". */
export function timeLabel(iso: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}

/** "Monday, January 11, 2027 at 10:30 AM EST". */
export function whenLabel(iso: string | Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(new Date(iso));
}

const dayKey = (iso: string, timeZone: string) => new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
const addDays = (day: string, n: number) => new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export function LocationNote({ location, durationMinutes }: { location: "video" | "phone"; durationMinutes: number }) {
  return (
    <span className="inline-flex items-center gap-3 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" /> {durationMinutes} min
      </span>
      <span className="inline-flex items-center gap-1">
        {location === "video" ? <Video className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
        {location === "video" ? "Video call" : "Phone call"}
      </span>
    </span>
  );
}

/**
 * Two weeks at a time: the days with open times, then the times of the chosen day. `load` fetches
 * the open start times (ISO) between two days.
 */
export function SlotPicker({ queryKey, load, timeZone, value, onChange }: { queryKey: string; load: (from: string, to: string) => Promise<string[]>; timeZone: string; value: string | null; onChange: (iso: string) => void }) {
  const today = dayKey(new Date().toISOString(), timeZone);
  const [from, setFrom] = useState(today);
  const to = addDays(from, 13);
  const { data, isLoading, error } = useQuery({ queryKey: ["slots", queryKey, from], queryFn: () => load(from, to) });
  const byDay = new Map<string, string[]>();
  for (const s of data ?? []) byDay.set(dayKey(s, timeZone), [...(byDay.get(dayKey(s, timeZone)) ?? []), s]);
  const days = [...byDay.keys()];
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked && byDay.has(picked) ? picked : (days[0] ?? null);
  const zoneName = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "long" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Button type="button" size="sm" variant="outline" aria-label="Earlier dates" disabled={from <= today} onClick={() => setFrom(addDays(from, -14) < today ? today : addDays(from, -14))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">
          {dayLabel(`${from}T12:00:00Z`, "UTC")} – {dayLabel(`${to}T12:00:00Z`, "UTC")}
        </span>
        <Button type="button" size="sm" variant="outline" aria-label="Later dates" onClick={() => setFrom(addDays(from, 14))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      {isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Finding open times…</p>
      ) : error ? (
        <p className="py-6 text-center text-sm text-destructive">Open times couldn't be loaded. Please try again.</p>
      ) : !days.length ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No open times these two weeks. Try later dates.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5" role="listbox" aria-label="Day">
            {days.map((d) => (
              <button
                key={d}
                type="button"
                role="option"
                aria-selected={d === day}
                onClick={() => setPicked(d)}
                className={cn("rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors", d === day ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50")}
              >
                {dayLabel(`${d}T12:00:00Z`, "UTC")}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4" role="listbox" aria-label="Time">
            {(day ? byDay.get(day)! : []).map((s) => (
              <button
                key={s}
                type="button"
                role="option"
                aria-selected={s === value}
                onClick={() => onChange(s)}
                className={cn("rounded-md border px-2 py-2 text-sm tabular transition-colors", s === value ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50")}
              >
                {timeLabel(s, timeZone)}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Times are {zoneName ?? timeZone}.</p>
        </>
      )}
    </div>
  );
}

/** A calendar link for Google Calendar, from the meeting's start and end. */
export function googleCalendarUrl(title: string, start: string | Date, end: string | Date, details: string) {
  const f = (t: string | Date) => new Date(t).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const p = new URLSearchParams({ action: "TEMPLATE", text: title, dates: `${f(start)}/${f(end)}`, details });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

/** The texts opt-in: unticked until the person ticks it, with the carriers' required wording. */
export function TextOptIn({ id, firm, checked, onChange }: { id: string; firm: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
      <label htmlFor={id} className="flex items-start gap-2.5 text-sm">
        <input id={id} type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{smsConsentText(firm)}</span>
      </label>
      <p className="mt-1 pl-[26px] text-xs text-muted-foreground">{SMS_DISCLOSURE}</p>
    </div>
  );
}
